import type { GameSession } from '../types/game';

export interface SaveSlot {
  id: number;
  timestamp: number;
  playerName: string;
  worldName: string;
  turnCount: number;
  session: GameSession;
  characterTraits?: string;
  selectedTheme?: string;
}

export class SaveManager {
  private static readonly SAVE_KEY_PREFIX = 'instantale_save_';
  private static readonly MAX_SLOTS = 4;

  /**
   * Save game to a specific slot
   */
  static saveGame(slotId: number, session: GameSession, characterTraits?: string, selectedTheme?: string): boolean {
    if (slotId < 1 || slotId > this.MAX_SLOTS) {
      console.error('[SaveManager] Invalid slot ID:', slotId);
      return false;
    }

    try {
      const saveData: SaveSlot = {
        id: slotId,
        timestamp: Date.now(),
        playerName: session.characters.find(c => c.alive)?.name || 'Unknown',
        worldName: session.world.name,
        turnCount: session.worldState.turnCount,
        session,
        characterTraits,
        selectedTheme
      };

      localStorage.setItem(
        `${this.SAVE_KEY_PREFIX}${slotId}`,
        JSON.stringify(saveData)
      );

      console.log('[SaveManager] Game saved to slot', slotId);
      return true;
    } catch (error) {
      console.error('[SaveManager] Save failed:', error);
      return false;
    }
  }

  /**
   * Load game from a specific slot
   */
  static loadGame(slotId: number): SaveSlot | null {
    if (slotId < 1 || slotId > this.MAX_SLOTS) {
      console.error('[SaveManager] Invalid slot ID:', slotId);
      return null;
    }

    try {
      const saveDataStr = localStorage.getItem(`${this.SAVE_KEY_PREFIX}${slotId}`);
      if (!saveDataStr) {
        return null;
      }

      const saveData: SaveSlot = JSON.parse(saveDataStr);
      console.log('[SaveManager] Game loaded from slot', slotId);
      return saveData;
    } catch (error) {
      console.error('[SaveManager] Load failed:', error);
      return null;
    }
  }

  /**
   * Get all save slots info
   */
  static getAllSaves(): (SaveSlot | null)[] {
    const saves: (SaveSlot | null)[] = [];
    for (let i = 1; i <= this.MAX_SLOTS; i++) {
      saves.push(this.loadGame(i));
    }
    return saves;
  }

  /**
   * Delete save from a specific slot
   */
  static deleteSave(slotId: number): boolean {
    if (slotId < 1 || slotId > this.MAX_SLOTS) {
      console.error('[SaveManager] Invalid slot ID:', slotId);
      return false;
    }

    try {
      localStorage.removeItem(`${this.SAVE_KEY_PREFIX}${slotId}`);
      console.log('[SaveManager] Save deleted from slot', slotId);
      return true;
    } catch (error) {
      console.error('[SaveManager] Delete failed:', error);
      return false;
    }
  }

  /**
   * Check if a slot has save data
   */
  static hasSave(slotId: number): boolean {
    return this.loadGame(slotId) !== null;
  }

  /**
   * Format timestamp to readable date
   */
  static formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
